import { App, Button } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, ImageCapture } from '@/components';
import { SurveyHttp } from '@/services';
import { ReactComponent as FaceImage } from '@/assets/images/face-recognation.svg';
import parseError from '@/utils/parseError';
import { useFaceErrorFn } from '@/hooks';

export interface SurveyFaceAuthProps {
  survey: SurveyEntity;
  onComplete: () => void;
}

export default function SurveyFaceAuth({ survey, onComplete }) {

  const message = App.useApp().message;
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [t] = useTranslation(['common', 'face', 'survey']);
  const parseFaceError = useFaceErrorFn();

  const handleUpload = useCallback(async (file: File) => {
    setUploading(true);
    try {
      await SurveyHttp.uploadImage(survey.id, file);
    }
    catch (e) {
      message.error(parseError(e, t('face:errors.upload-face')));
      return;
    }
    try {
      await SurveyHttp.verifyImage(survey.id);
      message.success(t('face:messages.success-face-enrollment'));
      if (onComplete) {
        onComplete();
      }
    }
    catch (e) {
      const err = parseError(e);
      const enrollmentError = parseFaceError(err, false);
      let finalError: string;
      if (enrollmentError) {
        finalError = enrollmentError;
      }
      else if (err) {
        finalError = err;
      }
      else {
        finalError = t('common:errors.not-defined-error');
      }
      message.error(finalError);
    }
    setUploading(false);
  }, [onComplete, survey, t, message, parseFaceError]);

  return (
    <Flex
      align="center"
      direction="column"
      className="bg-gray-100 p-2 rounded-lg h-full max-h-full max-w-sm mx-auto">
      {showCamera ? <ImageCapture loading={uploading} onConfirm={handleUpload} /> : (
        <Flex className="h-full mb-12" direction="column" align="center">
          <Flex
            direction="column"
            justify="center"
            className="w-full h-full">
            <FaceImage height="160px" />
            <p className="text-lg px-3 text-center leading-relaxed">
              {t('survey:messages.face-auth-required')}
            </p>
            <Button
              size="large"
              type="primary"
              className="mx-auto"
              onClick={() => setShowCamera(true)}
            >{t('face:labels.start-authentication')}</Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
